import { useEffect, useState } from "react";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import SectionEditModal from "@modals/SectionEditModal";
import SectionSelectModal from "@modals/SectionSelectModal";
import { filterActiveLanguages, getLanguageAbbrev, getLanguageDisplayName, type LanguageLike } from "@/utils/languageUtils";
import { apiMessage } from "@/utils/apiMessage";

export default function AdminSectionsTab(props: any) {
    const tabData = props?.tabData || props;

    if (!tabData) {
        console.error('AdminSectionsTab: props or props.tabData is undefined');
        return null;
    }

    const { updateSpinner, activeTab } = tabData;
    const [sections, setSections] = useState([]);
    const [showSectionEditModal, setShowSectionEditModal] = useState(false);
    const [showSectionSelectModal, setShowSectionSelectModal] = useState(false);
    const [selectedSectionId, setSelectedSectionId] = useState(null);
    const [selectedSectionName, setSelectedSectionName] = useState("");
    const [selectedSectionContentId, setSelectedSectionContentId] = useState(null);
    const [selectedCustomSectionId, setSelectedCustomSectionId] = useState(null);
    const [showAllSections, setShowAllSections] = useState(false);
    const [activeLanguages, setActiveLanguages] = useState<LanguageLike[]>([]);

    useEffect(() => {
        if (isOnThisTab()) {
            getSectionsAsync();
        }
    }, [activeTab]);

    if (!isOnThisTab()) return null;

    function isOnThisTab() {
        return props && activeTab && activeTab === "sections";
    }

    async function getSectionsAsync() {
        if (!isOnThisTab()) return;
        if (typeof updateSpinner === "function") updateSpinner(true);
        try {
            const [resp, langResp] = await Promise.all([
                RightTimeApi.getAsync("/Sections"),
                RightTimeApi.getAsync("/Languages?activeOnly=true"),
            ]);

            if (langResp?.ok && Array.isArray(langResp.jsonData)) {
                setActiveLanguages(langResp.jsonData);
            } else {
                setActiveLanguages([]);
            }

            if (!resp?.ok) {
                toast.error("Failed to load sections.");
                setSections([]);
                return;
            }

            const data = resp?.jsonData ?? resp?.data ?? [];
            const list = Array.isArray(data)
                ? data
                : Array.isArray(data?.sections)
                    ? data.sections
                    : [];

            setSections(list);

        } catch {
            toast.error("Error loading sections.");
            setSections([]);
            setActiveLanguages([]);
        } finally {
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    function getSectionId(section: any) {
        return Number(section?.sectionId || section?.id || 0) || null;
    }

    function isActiveSection(section: any) {
        if (typeof section?.archived === "boolean") return !section.archived;
        if (typeof section?.isArchived === "boolean") return !section.isArchived;
        if (typeof section?.archived === "number") return section.archived === 0;
        if (typeof section?.isArchived === "number") return section.isArchived === 0;
        if (typeof section?.archived === "string") {
            const normalized = section.archived.trim().toLowerCase();
            if (normalized === "true" || normalized === "1") return false;
            if (normalized === "false" || normalized === "0") return true;
        }
        if (typeof section?.isArchived === "string") {
            const normalized = section.isArchived.trim().toLowerCase();
            if (normalized === "true" || normalized === "1") return false;
            if (normalized === "false" || normalized === "0") return true;
        }
        if (typeof section?.active === "boolean") return section.active;
        if (typeof section?.isActive === "boolean") return section.isActive;
        return true;
    }

    function getClientErrorMessage(resp: any) {
        const status = Number(resp?.status || 0);
        if (status < 400 || status >= 500) return "";

        return apiMessage(resp);
    }

    function getActiveLanguageAbbrevSet() {
        return new Set(
            filterActiveLanguages(activeLanguages)
                .map((lang) => getLanguageAbbrev(lang))
                .filter((abbrev) => abbrev.length > 0)
        );
    }

    function filterToActiveLanguageAbbrevs(abbrevs: string[]) {
        const activeAbbrevs = getActiveLanguageAbbrevSet();
        if (activeAbbrevs.size === 0) return abbrevs;

        return abbrevs.filter((abbrev) => activeAbbrevs.has(abbrev.trim().toLowerCase()));
    }

    function getLanguageDisplayByAbbrev(abbrev: string) {
        const normalized = abbrev.trim().toLowerCase();
        const match = activeLanguages.find((lang) => getLanguageAbbrev(lang) === normalized);
        return match ? getLanguageDisplayName(match) : abbrev;
    }

    function getSectionLanguagesDisplay(section: any) {
        const rawLanguages = section?.languages || section?.Languages;
        if (Array.isArray(rawLanguages) && rawLanguages.length > 0) {
            const languages = filterToActiveLanguageAbbrevs(
                rawLanguages
                    .filter((lang) => typeof lang === "string" && lang.trim().length > 0)
                    .map((lang) => lang.trim())
            );
            return languages.map(getLanguageDisplayByAbbrev).join(", ");
        }

        const contentLanguages = Array.isArray(section?.content)
            ? filterToActiveLanguageAbbrevs(
                section.content
                    .map((item) => item?.abbrev)
                    .filter((abbr) => typeof abbr === "string" && abbr.trim().length > 0)
                    .map((abbr) => abbr.trim())
            )
            : [];

        return contentLanguages.length > 0
            ? contentLanguages.map(getLanguageDisplayByAbbrev).join(", ")
            : "";
    }

    async function toggleSectionActiveAsync(e: any) {
        e.stopPropagation();
        const sectionId = Number(e.currentTarget?.dataset?.id || 0) || null;
        if (!sectionId) return;

        if (typeof updateSpinner === "function") updateSpinner(true);
        try {
            let resp = await RightTimeApi.putAsync(`/Section/${sectionId}/ActiveToggle`);
            const firstErrorMsg = getClientErrorMessage(resp);
            if (!resp?.ok) {
                resp = await RightTimeApi.putAsync(`/Section/${sectionId}/ToggleActive`);
            }

            if (resp?.ok) {
                await getSectionsAsync();
                toast.success("Section updated.");
            } else {
                const finalErrorMsg = getClientErrorMessage(resp);
                toast.error(finalErrorMsg || firstErrorMsg || "Failed to update section.");
            }
        } catch {
            toast.error("Error updating section.");
        } finally {
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    const listedSections = showAllSections ? sections : sections.filter(isActiveSection);

    function onOpenSection(section: any) {
        const sectionId = getSectionId(section);
        if (!sectionId) return;

        if (typeof updateSpinner === "function") updateSpinner(true);

        setSelectedSectionId(sectionId);
        setSelectedSectionContentId(section.sectionContentId);
        setShowSectionEditModal(true);
    }

    function onCloseSectionEditModal() {
        if (typeof updateSpinner === "function") updateSpinner(false);
        setShowSectionEditModal(false);
        setSelectedSectionId(null);
        setSelectedSectionContentId(null);
    }

    function onOpenSectionSelectModal(e: any, section: any) {
        e.stopPropagation();

        const sectionId = getSectionId(section);
        if (!sectionId) return;

        const customSectionId = Number(
            section?.sectionContentId ||
            section?.SectionContentId ||
            section?.customSectionId ||
            section?.CustomSectionId ||
            0
        ) || null;

        if (typeof updateSpinner === "function") updateSpinner(true);

        setSelectedSectionId(sectionId);
        setSelectedSectionName(section?.name || section?.title || section?.sectionName || "");
        setSelectedCustomSectionId(customSectionId);
        setShowSectionSelectModal(true);
    }

    function onCloseSectionSelectModal() {
        if (typeof updateSpinner === "function") updateSpinner(false);
        setShowSectionSelectModal(false);
        setSelectedSectionId(null);
        setSelectedSectionName("");
        setSelectedCustomSectionId(null);
    }

    function getSelectedCustomContentId(customSection: any) {
        return Number(
            customSection?.sectionContentId ||
            customSection?.SectionContentId ||
            customSection?.customSectionId ||
            customSection?.CustomSectionId ||
            customSection?.id ||
            0
        ) || null;
    }

    function getSelectedCustomContentName(customSection: any) {
        return customSection?.customizedSectionName
            || customSection?.CustomName
            || customSection?.customName
            || customSection?.sectionContentName
            || customSection?.SectionContentName
            || customSection?.name
            || "";
    }

    async function onSelectCustomSection(customSection: any) {
        const sectionId = Number(selectedSectionId || 0) || null;
        const contentId = getSelectedCustomContentId(customSection);
        const contentName = getSelectedCustomContentName(customSection);

        if (!sectionId || !contentId) {
            toast.error("Unable to apply selected section content.");
            return;
        }

        if (typeof updateSpinner === "function") updateSpinner(true);
        try {
            let getResp = await RightTimeApi.getAsync(`/Section/${sectionId}`);

            if (!getResp?.ok) {
                toast.error("Failed to load section for update.");
                return;
            }

            const sectionData = getResp?.jsonData ?? getResp?.data ?? {};
            const loadedSection = sectionData?.currentSection || sectionData?.section || sectionData;
            if (!loadedSection || !Number(loadedSection?.id || sectionId)) {
                toast.error("Section data is missing.");
                return;
            }

            const payload = {
                ...loadedSection,
                id: Number(loadedSection?.id || sectionId),
                sectionContentId: contentId,
            };

            const putResp = await RightTimeApi.putAsync("/Section", payload);
            if (putResp?.ok) {
                setSections((prev) => prev.map((section) => {
                    const id = getSectionId(section);
                    if (id !== sectionId) return section;

                    return {
                        ...section,
                        sectionContentId: contentId,
                        SectionContentId: contentId,
                        sectionContentName: contentName,
                        SectionContentName: contentName,
                    };
                }));
                setSelectedCustomSectionId(contentId);
                toast.success("Section content selected.");
                onCloseSectionSelectModal();
            } else {
                toast.error("Failed to save section content selection.");
            }
        } catch {
            toast.error("Error saving section content selection.");
        } finally {
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    async function onDeselectCustomSection() {
        const sectionId = Number(selectedSectionId || 0) || null;

        if (!sectionId) {
            toast.error("Unable to clear selected section content.");
            return;
        }

        if (typeof updateSpinner === "function") updateSpinner(true);
        try {
            let getResp = await RightTimeApi.getAsync(`/Section/${sectionId}`);

            if (!getResp?.ok) {
                toast.error("Failed to load section for update.");
                return;
            }

            const sectionData = getResp?.jsonData ?? getResp?.data ?? {};
            const loadedSection = sectionData?.currentSection || sectionData?.section || sectionData;
            if (!loadedSection || !Number(loadedSection?.id || sectionId)) {
                toast.error("Section data is missing.");
                return;
            }

            const payload = {
                ...loadedSection,
                id: Number(loadedSection?.id || sectionId),
                sectionContentId: null,
            };

            const putResp = await RightTimeApi.putAsync("/Section", payload);
            if (putResp?.ok) {
                setSections((prev) => prev.map((section) => {
                    const id = getSectionId(section);
                    if (id !== sectionId) return section;

                    return {
                        ...section,
                        sectionContentId: null,
                        SectionContentId: null,
                        sectionContentName: "",
                        SectionContentName: "",
                    };
                }));
                setSelectedCustomSectionId(null);
                toast.success("Section content deselected.");
                onCloseSectionSelectModal();
            } else {
                toast.error("Failed to clear section content selection.");
            }
        } catch {
            toast.error("Error clearing section content selection.");
        } finally {
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    return (
        <>
            <div className="tableWrapper dimensionTable adminSectionsTable scrollable">
                <table>
                    <caption>
                      <span className="title">Sections</span>
                    </caption>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th
                                className="toggleColumnHeader clickable"
                                data-help-id="ShowSelectedOrAll"
                                onClick={() => setShowAllSections((prev) => !prev)}
                            >
                                {showAllSections ? "All" : "Active"}
                            </th>
                            <th>IsCounted</th>
                            <th>Name</th>
                            <th>Form ID</th>
                            <th>Pages</th>
                            <th>Languages</th>
                            <th>Default Content</th>
                        </tr>
                    </thead>
                    <tbody>
                        {listedSections.length > 0 ? (
                            listedSections.map((section, index) => {
                                const sectionId = getSectionId(section);
                                const sectionName = section?.name || section?.title || section?.sectionName || `Section ${index + 1}`;
                                const formId = Number(section?.formId || section?.FormId || 0) || "";
                                const pages = Number(section?.pages || section?.Pages || section?.pageCount || section?.PageCount || 0) || "";
                                const languagesDisplay = getSectionLanguagesDisplay(section);
                                const sectionContentName = section?.SectionContentName || section?.sectionContentName || "";
                                const rowKey = sectionId || `${sectionName}-${index}`;
                                const isActive = isActiveSection(section);
                                const isCounted = section?.isCounted === true || section?.IsCounted === true;
                                return (
                                    <tr
                                        key={rowKey}
                                        onClick={() => onOpenSection(section)}
                                        className={sectionId ? "clickableRow" : ""}
                                    >
                                        <td>{sectionId || ""}</td>
                                        <td onClick={toggleSectionActiveAsync} data-id={sectionId}>
                                            {isActive ? (
                                                <MdCheckBox className="actionIcon" />
                                            ) : (
                                                <MdCheckBoxOutlineBlank className="actionIcon" />
                                            )}
                                        </td>
                                        <td>
                                            {isCounted ? (
                                                <MdCheckBox className="actionIcon" title="IsCounted" />
                                            ) : (
                                                <MdCheckBoxOutlineBlank className="actionIcon" title="Not counted" />
                                            )}
                                        </td>
                                        <td>{sectionName}</td>
                                        <td>{formId}</td>
                                        <td>{pages}</td>
                                        <td>{languagesDisplay}</td>
                                        <td onClick={(e) => onOpenSectionSelectModal(e, section)}>
                                            {sectionContentName}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={8} className="centerAlignedCell">
                                    No sections found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <SectionEditModal
                isOpen={showSectionEditModal}
                onClose={onCloseSectionEditModal}
                sectionId={selectedSectionId}
                sectionContentId={selectedSectionContentId}
                updateSpinner={updateSpinner}
            />
            <SectionSelectModal
                isOpen={showSectionSelectModal}
                onClose={onCloseSectionSelectModal}
                onSelect={onSelectCustomSection}
                onDeselect={onDeselectCustomSection}
                sectionId={selectedSectionId}
                sectionName={selectedSectionName}
                customSectionId={selectedCustomSectionId}
                    companyId={null}
                updateSpinner={updateSpinner}
            />
        </>
    );
}
